import { useEffect, useRef } from "react";
import { useI18n } from "../i18n";

/** 확대 · 축소 배율 범위 · 한 번에 바뀌는 비율(렌더러 setZoom 이 같은 범위로 자른다) */
export const ZOOM_MIN = 0.4;
export const ZOOM_MAX = 3;
export const ZOOM_STEP = 1.25;
const clamp = (f: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, f));

export interface ZoomableView {
  setZoom?(factor: number): void;
}

/**
 * 3D 뷰 확대 · 축소(2026-10-01 요청) — ＋ · － 단추(터치 44px)와 캔버스 위 마우스 휠. 배율 = 카메라 거리(1 = 기본 · 작을수록 가깝다).
 * 뷰는 getView 로 그때그때 받는다(뷰가 다시 만들어지면 다음 누름부터 새 뷰에 걸린다). 배율은 이 화면에만 — 저장하지 않는다.
 */
export function ZoomControls({
  getView,
  wheelTarget,
}: {
  getView: () => ZoomableView | null;
  /** 휠을 받을 요소(캔버스) — 없으면 단추만 */
  wheelTarget?: React.RefObject<HTMLElement | null>;
}) {
  const { t } = useI18n();
  const zoom = useRef(1);
  const apply = (f: number) => {
    zoom.current = clamp(f);
    getView()?.setZoom?.(zoom.current);
  };
  const applyRef = useRef(apply);
  applyRef.current = apply;
  useEffect(() => {
    const el = wheelTarget?.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      applyRef.current(zoom.current * (e.deltaY > 0 ? ZOOM_STEP : 1 / ZOOM_STEP));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [wheelTarget]);
  return (
    <div className="pf-zoom" data-testid="view-zoom">
      <button
        type="button"
        className="pf-zoom__btn"
        aria-label={t("common.zoomIn")}
        title={t("common.zoomIn")}
        data-testid="zoom-in"
        onClick={() => apply(zoom.current / ZOOM_STEP)}
      >
        ＋
      </button>
      <button
        type="button"
        className="pf-zoom__btn"
        aria-label={t("common.zoomOut")}
        title={t("common.zoomOut")}
        data-testid="zoom-out"
        onClick={() => apply(zoom.current * ZOOM_STEP)}
      >
        －
      </button>
    </div>
  );
}
